import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const Testimonials = ({ 
  customTitle = "What Our Users Say", 
  customDescription = "Join thousands of professionals already transforming their networking experience with AM CARDMATE",
  customBadgeText = "8+ Reviews"
}) => {
  const trackRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [visibleCards, setVisibleCards] = useState(1);
 
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      
      if (width < 640) {
        setVisibleCards(1);
      } else if (width < 1024) {
        setVisibleCards(2);
      } else {
        setVisibleCards(3);
      }
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  
  const testimonials = [
    {
      initials: 'JD',
      name: 'John Doe',
      position: 'Marketing Director',
      text: 'AM CARDMATE has completely transformed how I network at events. The NFC card is a real conversation starter.',
      rating: 5,
      company: 'TechCorp'
    },
    {
      initials: 'SJ',
      name: 'Sarah Johnson',
      position: 'Business Owner',
      text: 'The collections feature is a game-changer for organizing contacts. No more stacks of paper cards!',
      rating: 4.5,
      company: 'Johnson Boutique'
    },
    {
      initials: 'MP',
      name: 'Michael Patel',
      position: 'Tech Entrepreneur',
      text: 'Our entire team uses AM CARDMATE. The wooden NFC cards align perfectly with our eco-friendly brand.',
      rating: 5,
      company: 'EcoTech'
    },
    {
      initials: 'AL',
      name: 'Amy Lawrence',
      position: 'Event Coordinator',
      text: 'AM CARDMATE has made networking at our events much more efficient. The analytics are incredibly valuable.',
      rating: 5,
      company: 'EventPro'
    },
    {
      initials: 'RK',
      name: 'Robert Kim',
      position: 'Sales Manager',
      text: 'I love how professional the wooden NFC cards look. They always leave a lasting impression on clients.',
      rating: 5,
      company: 'Global Sales Inc'
    },
    {
      initials: 'EW',
      name: 'Emma Williams',
      position: 'Freelance Designer',
      text: 'The customization options are fantastic! My digital card perfectly matches my brand aesthetic.',
      rating: 4.5,
      company: 'EW Design Studio'
    },
    {
      initials: 'TM',
      name: 'Thomas Martinez',
      position: 'CEO',
      text: 'Switching to AM CARDMATE saved our company thousands on printing costs. Plus, we can update info instantly!',
      rating: 5,
      company: 'Martinez Group'
    },
    {
      initials: 'LC',
      name: 'Lisa Chen',
      position: 'Real Estate Agent',
      text: 'The QR code sharing feature is perfect for open houses. Clients can save my contact info in seconds.',
      rating: 4.5,
      company: 'Premier Realty'
    }
  ];

  const scrollLeft = () => {
    if (trackRef.current) {
      const cardWidth = trackRef.current.querySelector('.testimonial-card').offsetWidth;
      const gap = window.innerWidth < 640 ? 16 : window.innerWidth < 768 ? 20 : 24;
      const scrollAmount = cardWidth + gap; 
      
      trackRef.current.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (trackRef.current) {
      const cardWidth = trackRef.current.querySelector('.testimonial-card').offsetWidth;
      const gap = window.innerWidth < 640 ? 16 : window.innerWidth < 768 ? 20 : 24;
      const scrollAmount = cardWidth + gap; 
      
      trackRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="testimonials" className="py-12 sm:py-16 md:py-24 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-50/40 via-transparent to-blue-50/30"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_10%_10%,rgba(77,162,255,0.04)_0%,transparent_50%),radial-gradient(ellipse_at_90%_90%,rgba(222,97,97,0.04)_0%,transparent_50%)]"></div>
      
      {/* Animated Blobs */}
      <motion.div 
        className="absolute top-1/4 left-1/6 w-32 h-32 sm:w-64 sm:h-64 rounded-full bg-blue-200/5 filter blur-3xl"
        animate={{ 
          scale: [1, 1.1, 1],
          x: [0, 30, 0],
          y: [0, -20, 0],
        }}
        transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
      />
      
      <motion.div 
        className="absolute bottom-1/4 right-1/6 w-32 h-32 sm:w-64 sm:h-64 rounded-full bg-pink-200/5 filter blur-3xl"
        animate={{ 
          scale: [1.2, 1, 1.2],
          x: [0, -25, 0],
          y: [0, 30, 0],
        }}
        transition={{ duration: 25, repeat: Infinity, ease: "easeInOut", delay: 5 }}
      />
      
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12 md:mb-16">
          <div className="badge badge-blue inline-flex mb-4">
            <FontAwesomeIcon icon="heart" />
            <span>{customBadgeText}</span>
          </div>
          
          <h2 className="text-h2 mb-4 md:mb-6 leading-tight">
            {customTitle.split(' ').map((word, i, arr) => 
              i === arr.length - 1 ? 
                <span key={i} className="text-gradient-blue-pink">{word} </span> : 
                <span key={i}>{word} </span>
            )}
          </h2>
          
          <p className="text-body-lg text-gray-600 mb-4 leading-relaxed px-2 sm:px-0">
            {customDescription}
          </p>
          
          <div className="flex items-center justify-center gap-2 text-body-sm text-gray-600">
            <span className="font-medium">Thousands of professionals</span>
          </div>
        </div>
        
        {/* Testimonials Carousel */}
        <div className="relative mt-8 md:mt-10">
          {/* Progress Dots */}
          <div className="hidden lg:flex justify-center mb-4">
            <div className="flex gap-1">
              {[...Array(8)].map((_, i) => (
                <div 
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full ${i < 3 ? 'bg-primary-blue' : 'bg-gray-300'}`}
                />
              ))}
            </div>
          </div>
          
          {/* Cards Track */}
          <div 
            ref={trackRef}
            className="flex gap-3 sm:gap-4 md:gap-6 overflow-x-auto pb-3 sm:pb-4 md:pb-6 pt-2 px-2 sm:px-4 snap-x snap-mandatory scrollbar-hide scroll-smooth -mx-2 sm:-mx-4 md:-mx-6"
          >
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="testimonial-card bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-5 md:p-6 lg:p-8 min-w-[260px] sm:min-w-[270px] md:min-w-[340px] lg:min-w-[380px] xl:min-w-[400px] max-w-[280px] sm:max-w-[420px] flex-none snap-center relative overflow-hidden transition-all duration-500 flex flex-col hover:translate-y-[-6px] sm:hover:translate-y-[-8px] hover:scale-[1.01] sm:hover:scale-[1.02] hover:bg-white"
                style={{
                  boxShadow: `0 6px 24px sm:0 8px 32px ${
                    index % 4 === 0 ? 'rgba(77, 162, 255, 0.08)' :
                    index % 4 === 1 ? 'rgba(168, 85, 247, 0.08)' :
                    index % 4 === 2 ? 'rgba(20, 184, 166, 0.08)' :
                    'rgba(249, 115, 22, 0.08)'
                  }, 0 2px 8px rgba(0, 0, 0, 0.02)`
                }}
              >
                {/* Card Gradient Overlay */}
                <div className={`absolute inset-0 bg-gradient-to-br ${
                  index % 4 === 0 ? 'from-blue-500/5 via-transparent to-indigo-500/5' :
                  index % 4 === 1 ? 'from-purple-500/5 via-transparent to-pink-500/5' :
                  index % 4 === 2 ? 'from-teal-500/5 via-transparent to-cyan-500/5' :
                  'from-orange-500/5 via-transparent to-red-500/5'
                } opacity-40`}></div>
                
                <div className="relative z-10">
                  {/* User Info */}
                  <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-5">
                    <div 
                      className={`w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br ${
                        index % 4 === 0 ? 'from-blue-500 to-indigo-600' :
                        index % 4 === 1 ? 'from-purple-500 to-pink-600' :
                        index % 4 === 2 ? 'from-teal-500 to-cyan-600' :
                        'from-orange-500 to-red-600'
                      } flex items-center justify-center text-white font-display text-body font-bold shadow-lg`}
                    >
                      {testimonial.initials}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="text-card-title text-gray-900 truncate">{testimonial.name}</div>
                      <div className="text-caption text-gray-600 flex items-center gap-1 sm:gap-2">
                        <span className="truncate">{testimonial.position}</span>
                        <span className="text-xs opacity-40 hidden sm:inline">•</span>
                        <span className={`${
                          index % 4 === 0 ? 'text-blue-600' :
                          index % 4 === 1 ? 'text-purple-600' :
                          index % 4 === 2 ? 'text-teal-600' :
                          'text-orange-600'
                        } font-medium truncate hidden sm:inline`}>{testimonial.company}</span>
                      </div>
                      {/* Mobile Company */}
                      <div className="sm:hidden">
                        <span className={`text-caption ${
                          index % 4 === 0 ? 'text-blue-600' :
                          index % 4 === 1 ? 'text-purple-600' :
                          index % 4 === 2 ? 'text-teal-600' :
                          'text-orange-600'
                        } font-medium truncate`}>{testimonial.company}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Testimonial Text */}
                  <div className="mb-4 sm:mb-5 flex-grow">
                    <p className="text-body text-gray-700 leading-relaxed font-medium">
                      "{testimonial.text}"
                    </p>
                  </div>
                  
                  {/* Rating */}
                  <div className="flex justify-start items-center">
                    <div className={`${
                      testimonial.rating === 5 ? 'text-amber-400' : 'text-amber-400/90'
                    } text-body-sm flex gap-0.5`}>
                      {[...Array(Math.floor(testimonial.rating))].map((_, i) => (
                        <FontAwesomeIcon key={i} icon="star" />
                      ))}
                      {testimonial.rating % 1 === 0.5 && (
                        <FontAwesomeIcon icon="star-half-alt" />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Navigation Buttons */}
          <div className="hidden sm:flex justify-center mt-6 md:mt-8 gap-4">
            <button 
              className="w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/90 backdrop-blur-sm text-gray-700 flex items-center justify-center text-lg focus:outline-none transition-all duration-300 group hover:scale-110 hover:bg-primary-blue hover:text-white"
              onClick={scrollLeft}
              aria-label="Previous testimonial"
              style={{
                boxShadow: "0 4px 20px rgba(77, 162, 255, 0.15)"
              }}
            >
              <FontAwesomeIcon icon="arrow-left" className="group-hover:-translate-x-0.5 transition-transform duration-300" />
            </button>
            
            <button 
              className="w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/90 backdrop-blur-sm text-gray-700 flex items-center justify-center text-lg focus:outline-none transition-all duration-300 group hover:scale-110 hover:bg-primary-blue hover:text-white"
              onClick={scrollRight}
              aria-label="Next testimonial"
              style={{
                boxShadow: "0 4px 20px rgba(77, 162, 255, 0.15)"
              }}
            >
              <FontAwesomeIcon icon="arrow-right" className="group-hover:translate-x-0.5 transition-transform duration-300" />
            </button>
          </div>
          
          {/* Mobile Swipe Hint */}
          <div className="flex sm:hidden justify-center mt-4">
            <p className="text-caption text-gray-500 flex items-center gap-1 sm:gap-2">
              <FontAwesomeIcon icon="hand-pointer" className="text-xs" />
              <span>Swipe for more</span>
            </p>
          </div>
        </div>
        
        {/* CTA Button */}
        <div className="flex justify-center mt-8 md:mt-12">
          <a 
            href="/personal-card" 
            className="btn btn-primary group relative overflow-hidden px-4 sm:px-6 py-2.5 sm:py-3 rounded-full hover:scale-105 hover:shadow-[0_12px_28px_rgba(77,162,255,0.25)] transition-all duration-300"
          >
            <span className="relative z-10 flex items-center gap-1.5 sm:gap-2 text-btn">
              <FontAwesomeIcon icon="info-circle" />
              <span>Learn More</span>
              <FontAwesomeIcon icon="arrow-right" className="ml-1 opacity-0 -translate-x-1 sm:-translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
            </span>
            <div
              className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-300"
            />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;